import { Button, DataTable, ResultsCard, ResultsToolbar } from 'shared/components/ui';
import React, { useState, useEffect } from 'react';

import { ViewColumn, ViewWeek, UnfoldMore } from '@mui/icons-material';
import PracticaRow from './PracticaRow';
import { contarEstados, getEstadoBadge } from '../../turnos/utils/turnoUtils';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import useDragScroll from '../../../shared/hooks/useDragScroll';
import './TablaPracticas.css';

const TablaPracticas = ({ turnos = [], procesadores, onEditarTurno, onNotificarTurno, loading }) => {
  const turnosArray = Array.isArray(turnos) ? turnos : [];
  const { sortedData, requestSort, getSortIcon } = useSortableTable(turnosArray);
  const estadosConteo = contarEstados(turnosArray);
  const { ref: scrollRef, onMouseDown, onMouseUp, onMouseMove, onMouseLeave } = useDragScroll();
  const [mostrarTodasColumnas, setMostrarTodasColumnas] = useState(() => {
    if (window.innerWidth <= 768) return false;
    const stored = localStorage.getItem('mostrarTodasColumnasPracticas');
    return stored !== null ? stored === 'true' : true;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setMostrarTodasColumnas(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth > 768) {
      localStorage.setItem('mostrarTodasColumnasPracticas', mostrarTodasColumnas.toString());
    }
  }, [mostrarTodasColumnas]);

  return (
    <ResultsCard className="results-card">
      <ResultsToolbar className="results-header">
        <div className="results-header-left">
          <h3 className="results-title">Resultados</h3>
          <div className="estado-badges">
            {Object.entries(estadosConteo).map(([estado, cantidad]) => (
              <span key={estado} className={`estado-count estado-count-${getEstadoBadge(estado)}`}>
                {cantidad} {estado}
              </span>
            ))}
          </div>
        </div>
        <div className="results-info">
          <span className="results-count">{turnosArray.length} prácticas encontradas</span>
          {loading && <span className="loading-indicator">Actualizando...</span>}
          <Button
            variant="outline-primary" 
            size="sm" 
            onClick={() => setMostrarTodasColumnas(!mostrarTodasColumnas)}
            className="btn-toggle-columns"
            style={{ display: window.innerWidth <= 768 ? 'none' : undefined }}
          >
            {mostrarTodasColumnas ? <ViewWeek sx={{ fontSize: 18, mr: 0.5 }} /> : <ViewColumn sx={{ fontSize: 18, mr: 0.5 }} />}
            {mostrarTodasColumnas ? 'Ocultar columnas' : 'Ver todas las columnas'}
          </Button>
        </div>
      </ResultsToolbar>
      <div
        ref={scrollRef}
        className="table-container table-container-scrollable"
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        style={{ cursor: 'grab' }}
      >
        <DataTable preservePinnedColumns className="data-table">
          <colgroup>
            <col style={{ width: '50px' }} />   {/* Nro */}
            <col style={{ width: '90px' }} />   {/* DNI */}
            <col style={{ width: '180px' }} />  {/* Paciente */}
            <col style={{ width: '55px' }} />   {/* Edad */}
            <col style={{ width: '110px' }} />  {/* Teléfono */}
            <col style={{ width: '140px' }} />  {/* Estudio */}
            <col style={{ width: '95px' }} />   {/* Fecha */}
            {mostrarTodasColumnas && (
              <>
                <col style={{ width: '110px' }} />  {/* Horario */}
                <col style={{ width: '100px' }} />  {/* E. Registro */}
                <col style={{ width: '100px' }} />  {/* E. CPAP */}
                <col style={{ width: '105px' }} />  {/* Fecha Entrega */}
                <col style={{ width: '130px' }} />  {/* Obra Social */}
                <col style={{ width: '80px' }} />   {/* Urgente */}
              </>
            )}
            <col style={{ width: '120px' }} />  {/* Estado */}
            <col style={{ width: '85px' }} />   {/* Acciones */}
          </colgroup>
          <thead>
            <tr>
              <th onClick={() => requestSort('idTurno')} style={{ cursor: 'pointer' }}>
                Nro {getSortIcon('idTurno') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
              </th>
              <th onClick={() => requestSort('persona.dni')} style={{ cursor: 'pointer' }}>
                DNI {getSortIcon('persona.dni') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
              </th>
              <th onClick={() => requestSort('persona.apellido')} style={{ cursor: 'pointer' }}>
                Paciente {getSortIcon('persona.apellido') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
              </th>
              <th onClick={() => requestSort('persona.fechaNacimiento')} style={{ cursor: 'pointer' }}>
                Edad {getSortIcon('persona.fechaNacimiento') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
              </th>
              <th onClick={() => requestSort('persona.telCelular')} style={{ cursor: 'pointer' }}>
                Teléfono {getSortIcon('persona.telCelular') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
              </th>
              <th onClick={() => requestSort('tipoEstudio.tipoEstudio')} style={{ cursor: 'pointer' }}>
                Estudio {getSortIcon('tipoEstudio.tipoEstudio') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
              </th>
              <th onClick={() => requestSort('horario.inicio')} style={{ cursor: 'pointer' }}>
                Fecha {getSortIcon('horario.inicio') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
              </th>
              {mostrarTodasColumnas && (
                <>
                  <th>Horario</th>
                  <th onClick={() => requestSort('idEquipoNavigation.equipo1')} style={{ cursor: 'pointer' }}>
                    E. Registro {getSortIcon('idEquipoNavigation.equipo1') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
                  </th>
                  <th onClick={() => requestSort('idEquipoCpapNavigation.equipo1')} style={{ cursor: 'pointer' }}>
                    E. CPAP {getSortIcon('idEquipoCpapNavigation.equipo1') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
                  </th>
                  <th onClick={() => requestSort('fechaEntrega')} style={{ cursor: 'pointer' }}>
                    Fecha Entrega {getSortIcon('fechaEntrega') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
                  </th>
                  <th onClick={() => requestSort('persona.obraSocial.nombre')} style={{ cursor: 'pointer' }}>
                    Obra Social {getSortIcon('persona.obraSocial.nombre') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
                  </th>
                  <th onClick={() => requestSort('urgente')} style={{ cursor: 'pointer' }}>
                    Urgente {getSortIcon('urgente') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
                  </th>
                </>
              )}
              <th onClick={() => requestSort('estadosTurno.estadoTurno')} style={{ cursor: 'pointer' }} className="estado-cell">
                Estado {getSortIcon('estadosTurno.estadoTurno') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4, ml: 0.5 }} />}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {turnosArray.length === 0 ? (
              <tr>
                <td colSpan={mostrarTodasColumnas ? "15" : "9"} className="empty-state">
                  {loading ? 'Cargando prácticas...' : 'No hay prácticas para mostrar'}
                </td>
              </tr>
            ) : (
              sortedData.map((turno, index) => (
                <PracticaRow
                  key={turno.idTurno || index}
                  turno={turno}
                  procesadores={procesadores}
                  onEditar={onEditarTurno}
                  onNotificar={onNotificarTurno}
                  loading={loading}
                  mostrarTodasColumnas={mostrarTodasColumnas}
                />
              ))
            )}
          </tbody>
        </DataTable>
      </div>
    </ResultsCard>
  );
};

export default TablaPracticas;